import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isEqual } from 'lodash'
import { Button } from '@node-space/storybook-components/dist/Button'
import { Callout } from '@node-space/storybook-components/dist/Callout'
import { Icon } from '@node-space/storybook-components/dist/Icon'
import { SkeletonForm } from '@node-space/storybook-components/dist/SkeletonLoader'
import { logSentryErrorAndGetTraceId } from '@node-space/utils'
import { useVerificationContext } from 'hooks/context/useVerificationContext'
import { useBusinessDirectorsMutation } from 'hooks/mutations/useBusinessDirectorsMutation'
import { useBusinessDirectorsQuery } from 'hooks/queries/useBusinessDirectorsQuery'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { BaseErrorResponse } from 'types/errors'
import { BusinessDirector } from 'types/verification'
import { FormBody } from '../_components/FormBody'
import { FormGetError } from '../_components/FormGetError'
import { FormSubmitButton } from '../_components/FormSubmitButton'
import { FormSubmitError } from '../_components/FormSubmitError'
import { NameTile, NameTilesContanier } from '../_components/NameTile'
import { VerificationHeader } from '../_components/VerificationHeader'
import { BusinessDirectorModal, DirectorModalData } from './BusinessDirectorModal'

export const TEMP_ID_TAG = 'tempId - '

export const BusinessDirectors = () => {
  const { t } = useTranslation()

  const initialDirector: DirectorModalData = {
    id: null,
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    address1: '',
    address2: '',
    city: '',
    postalCode: '',
    countryCode: '',
  }

  const [isEditingDirector, setIsEditingDirector] = useState(false)
  const [isEmptyError, setIsEmptyError] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedDirectorData, setSelectedDirectorData] =
    useState<DirectorModalData>(initialDirector)
  const [initialBusinessDirectors, setInitialBusinessDirectors] = useState<DirectorModalData[]>([])
  const [businessDirectors, setBusinessDirectors] = useState<DirectorModalData[]>([])

  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()
  const { setUnsavedChanges, submitStep } = useVerificationContext()

  const {
    data: businessDirectorsData,
    isFetching: isFetchingBusinessDirectors,
    isError: isGetError,
  } = useBusinessDirectorsQuery()

  const { mutate: putBusinessDirectors, isLoading: isLoadingSubmitRequest } =
    useBusinessDirectorsMutation()

  const isTempId = (id: string | number): boolean =>
    typeof id === 'string' && id.includes(TEMP_ID_TAG)

  useEffect(() => {
    if (!isFetchingBusinessDirectors && !!businessDirectorsData?.length) {
      const loadedDirectors: DirectorModalData[] = [...businessDirectorsData]?.map(directorData => {
        return {
          id: directorData?.id,
          firstName: directorData?.firstName ?? '',
          lastName: directorData?.lastName ?? '',
          dateOfBirth: directorData.dateOfBirth ?? '',
          address1: directorData?.address1 ?? '',
          address2: directorData?.address2 ?? '',
          city: directorData?.city ?? '',
          postalCode: directorData?.postalCode ?? '',
          countryCode: directorData?.countryCode ?? '',
        }
      })
      setInitialBusinessDirectors(loadedDirectors)
      setBusinessDirectors(loadedDirectors)
    }
  }, [isFetchingBusinessDirectors])

  const handleContinue = () => {
    resetRequestError()

    if (!businessDirectors?.length) {
      setIsEmptyError(true)
      return
    }

    const data: BusinessDirector[] = [...businessDirectors]?.map(({ id, ...directorData }) => ({
      ...(!isTempId(id) && { id: Number(id) }),
      firstName: directorData?.firstName,
      lastName: directorData?.lastName,
      dateOfBirth: directorData?.dateOfBirth,
      address1: directorData?.address1,
      address2: directorData?.address2,
      city: directorData?.city,
      postalCode: directorData?.postalCode,
      countryCode: directorData?.countryCode,
    }))

    putBusinessDirectors(data, {
      onSuccess: () => {
        submitStep('Business directors', 'accountRepresentative')
      },
      onError: (error: BaseErrorResponse) => {
        const sentryTraceId = logSentryErrorAndGetTraceId('putBusinessDirectors', error)
        setRequestError({
          errorCode: error?.status,
          sentryTraceId,
          show: true,
        })
      },
    })
  }

  useEffect(() => {
    const isChanged = !isEqual(businessDirectors, initialBusinessDirectors)
    setUnsavedChanges(isChanged)
  }, [businessDirectors])

  const resetModalData = () => {
    setIsEditingDirector(false)
    setSelectedDirectorData(initialDirector)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    resetModalData()
  }

  const onSaveDirector = (savedDirectorData: DirectorModalData) => {
    if (isEmptyError) {
      setIsEmptyError(false)
    }

    let updatedDirectors
    setBusinessDirectors(currentDirectors => {
      if (!isEditingDirector) {
        updatedDirectors = [...currentDirectors, savedDirectorData]
      } else {
        updatedDirectors = [...currentDirectors]
        const editedDirectorIndex = updatedDirectors.findIndex(
          director => director?.id === savedDirectorData.id
        )
        updatedDirectors[editedDirectorIndex] = savedDirectorData
      }
      return updatedDirectors
    })
    handleModalClose()
  }

  const onEditDirector = useCallback(
    directorId => {
      const directorIndex = businessDirectors.findIndex(director => director.id === directorId)
      setSelectedDirectorData(businessDirectors[directorIndex])
      setIsEditingDirector(true)
      setIsModalOpen(true)
    },
    [businessDirectors]
  )

  const onDeleteDirector = useCallback(
    directorId => {
      const updatedList = [...businessDirectors].filter(director => director.id !== directorId)
      setBusinessDirectors(updatedList)
      if (isEditingDirector) {
        handleModalClose()
      }
    },
    [businessDirectors, isEditingDirector]
  )

  return (
    <>
      <VerificationHeader
        header={t('verification.routes.businessDirectors')}
        subHeader={t('verification.businessDirectors.subHeader')}
      />

      {isFetchingBusinessDirectors ? (
        <SkeletonForm name="Business directors" rowCount={1} />
      ) : isGetError ? (
        <FormGetError />
      ) : (
        <FormBody>
          {requestError?.show && (
            <FormSubmitError
              errorCode={requestError?.errorCode}
              sentryTraceId={requestError?.sentryTraceId}
            />
          )}

          {!!businessDirectors?.length && (
            <NameTilesContanier>
              {businessDirectors?.map((director, index) => (
                <NameTile
                  key={`${director.firstName}-${index}`}
                  tileId={index}
                  label={`${director.firstName} ${director.lastName}`}
                  onEdit={() => onEditDirector(director?.id)}
                  onDelete={() => onDeleteDirector(director?.id)}
                />
              ))}
            </NameTilesContanier>
          )}

          {isEmptyError && (
            <Callout
              message={t('accountRepresentative.noAddedDirectors')}
              state="error"
              showCloseButton={false}
            />
          )}

          <Button
            testid="addDirector"
            onClick={() => {
              setIsModalOpen(true)
              setIsEditingDirector(false)
            }}
            secondary
            iconElement={<Icon name="PlusIcon" color="primary-500" />}
            className="w-fit"
          >
            {`${t('addDirector')}`}
          </Button>

          <FormSubmitButton
            loading={isLoadingSubmitRequest}
            disabled={isEmptyError}
            onClick={handleContinue}
          />
        </FormBody>
      )}

      {isModalOpen && (
        <BusinessDirectorModal
          directorData={selectedDirectorData}
          isEditingDirector={isEditingDirector}
          onSaveDirector={onSaveDirector}
          onDeleteDirector={onDeleteDirector}
          onClose={handleModalClose}
        />
      )}
    </>
  )
}

export default BusinessDirectors
